/**
 * HTTP client for the FastAPI service in /server.
 *
 * Nothing calls axios directly — the api/* modules use this so that auth,
 * error shaping and correlation IDs live in exactly one place.
 */
import axios, { AxiosError, type AxiosInstance, type AxiosRequestConfig } from "axios";
import { env } from "./env";

/** Shape FastAPI returns for HTTPException and validation failures. */
interface FastApiErrorBody {
  detail?: string | { msg: string; loc?: (string | number)[] }[];
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly fieldErrors: Record<string, string>;

  constructor(message: string, status: number, code: string, fieldErrors = {}) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.code = code;
    this.fieldErrors = fieldErrors;
  }

  /** 4xx other than 408/429 will not succeed on retry. */
  get isRetryable(): boolean {
    if (this.status === 0) return true;
    if (this.status === 408 || this.status === 429) return true;
    return this.status >= 500;
  }
}

function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error;

  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<FastApiErrorBody>;
    const status = axiosError.response?.status ?? 0;
    const detail = axiosError.response?.data?.detail;

    // FastAPI validation errors arrive as a list of {loc, msg}.
    if (Array.isArray(detail)) {
      const fieldErrors: Record<string, string> = {};
      for (const item of detail) {
        const field = item.loc?.filter((p) => p !== "body").join(".") ?? "";
        if (field) fieldErrors[field] = item.msg;
      }
      return new ApiError("Please check the highlighted fields.", status, "validation", fieldErrors);
    }

    if (typeof detail === "string") {
      return new ApiError(detail, status, "http");
    }

    if (status === 0) {
      return new ApiError(
        "We could not reach the server. Check your connection and try again.",
        0,
        "network",
      );
    }

    return new ApiError(axiosError.message, status, "http");
  }

  return new ApiError(
    error instanceof Error ? error.message : "Something went wrong.",
    0,
    "unknown",
  );
}

export const http: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 15_000,
  headers: { "Content-Type": "application/json" },
  // Enable once the backend issues session cookies.
  withCredentials: false,

  /**
   * Repeat the key for array values: `?status=a&status=b`.
   *
   * Axios defaults to `status[]=a&status[]=b`, which FastAPI reads as a
   * parameter literally named "status[]" — so it silently ignores it and
   * returns the *unfiltered* list. Nothing errors; the user just sees every
   * result with their filter chips lit up. Every multi-select on the site
   * depends on this serialiser.
   */
  paramsSerializer: {
    serialize: (params: Record<string, unknown>) => {
      const search = new URLSearchParams();
      for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;
        if (Array.isArray(value)) {
          for (const item of value) search.append(key, String(item));
        } else {
          search.append(key, String(value));
        }
      }
      return search.toString();
    },
  },
});

http.interceptors.request.use((config) => {
  // Correlation id lets a failed request be traced through the FastAPI logs.
  config.headers.set("X-Request-Id", crypto.randomUUID());
  return config;
});

http.interceptors.response.use((response) => {
  /**
   * Reject a 2xx that is not JSON.
   *
   * A static host with SPA fallback routing answers *every* unmatched path with
   * `200 text/html` — including `/api/projects`. Axios sees a success and hands
   * the caller index.html as a string, so the failure only surfaces much later
   * as `(data ?? []).filter is not a function` in whichever component happened
   * to touch it first. That error names the wrong file and says nothing about
   * the cause.
   *
   * This is never a legitimate response to an API call, so fail here, where the
   * message can point at the actual problem.
   */
  const contentType = String(response.headers?.["content-type"] ?? "");

  if (response.config.responseType === undefined && !contentType.includes("json")) {
    const target = `${response.config.baseURL ?? ""}${response.config.url ?? ""}`;
    throw new ApiError(
      `The API returned ${contentType || "an unknown content type"} instead of JSON ` +
        `(${target}). The app is probably pointed at a host with no backend — ` +
        `check VITE_API_BASE_URL, or set VITE_USE_MOCKS=true to run on demo data.`,
      response.status,
      "http",
    );
  }

  return response;
}, (error: unknown) => Promise.reject(toApiError(error)));

/** Thin helpers so call sites read as `get<Thing>("/things")`. */
export async function get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.get<T>(url, config);
  return data;
}

export async function post<T>(url: string, body?: unknown, config?: AxiosRequestConfig): Promise<T> {
  const { data } = await http.post<T>(url, body, config);
  return data;
}
